import React, { useState, useEffect } from 'react';
import { db } from '../firebase-config';
import { collection, getDocs, onSnapshot, query, orderBy, where, limit, doc, updateDoc, deleteDoc, addDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import RBACManager from './RBACManager';
import CRMContactModal from './CRMContactModal';
import CRMOpportunityModal from './CRMOpportunityModal';
import CRMPipelineModal from './CRMPipelineModal';
import LeadManagementDashboard from './LeadManagementDashboard';
import { 
  COLLECTIONS, 
  CONTACT_TEMPLATE, 
  LEAD_TEMPLATE, 
  OPPORTUNITY_TEMPLATE, 
  PIPELINE_TEMPLATE,
  CAMPAIGN_TEMPLATE,
  ACTIVITY_TEMPLATE,
  DEFAULT_PIPELINES,
  createContact,
  createLead,
  createOpportunity,
  createPipeline,
  initializeDefaultPipelines
} from '../utils/crmFirebaseStructure';

const HoppXGHLPro = () => {
  const [activeModule, setActiveModule] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // CRM Modal states
  const [showContactModal, setShowContactModal] = useState(false);
  const [showOpportunityModal, setShowOpportunityModal] = useState(false);
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [selectedPipeline, setSelectedPipeline] = useState(null);

  // Main modules configuration
  const modules = [
    {
      id: 'dashboard',
      title: 'Irányítópult',
      icon: '📊',
      color: 'from-blue-500 to-indigo-600',
      description: 'Áttekintő dashboard'
    },
    {
      id: 'conversations',
      title: 'Beszélgetések',
      icon: '💬',
      color: 'from-cyan-500 to-blue-600',
      description: 'Ügyfél kommunikáció'
    },
    {
      id: 'calendars',
      title: 'Naptárak',
      icon: '📅',
      color: 'from-yellow-500 to-orange-500',
      description: 'Időpontfoglalás'
    },
    {
      id: 'contacts',
      title: 'Kontaktok',
      icon: '👥',
      color: 'from-green-500 to-emerald-600',
      description: 'Kontakt kezelés és CRM'
    },
    {
      id: 'opportunities',
      title: 'Lehetőségek',
      icon: '💼',
      color: 'from-orange-500 to-red-600',
      description: 'Értékesítési lehetőségek'
    },
    {
      id: 'leads',
      title: 'HoppHomes Leadek',
      icon: '🏠',
      color: 'from-yellow-400 to-orange-500',
      description: 'Lead kvalifikáció'
    },
    {
      id: 'payments',
      title: 'Fizetések',
      icon: '💳',
      color: 'from-emerald-500 to-teal-500',
      description: 'Fizetések és számlázás'
    },
    {
      id: 'marketing',
      title: 'Marketing',
      icon: '📢',
      color: 'from-pink-500 to-rose-600',
      description: 'Marketing kampányok'
    },
    {
      id: 'automations',
      title: 'Automatizálás',
      icon: '🤖',
      color: 'from-purple-500 to-violet-600',
      description: 'Workflow automatizáció'
    },
    {
      id: 'sites',
      title: 'Webhelyek',
      icon: '🌐',
      color: 'from-blue-500 to-cyan-600',
      description: 'Weboldal építő'
    },
    {
      id: 'reputation',
      title: 'Hírnév',
      icon: '⭐',
      color: 'from-amber-500 to-yellow-500',
      description: 'Vélemény kezelés'
    },
    {
      id: 'reporting',
      title: 'Jelentések',
      icon: '📈',
      color: 'from-rose-500 to-pink-500',
      description: 'Jelentések és elemzések'
    },
    {
      id: 'settings',
      title: 'Beállítások',
      icon: '⚙️',
      color: 'from-gray-500 to-gray-600',
      description: 'Rendszer beállítások'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 p-6 rounded-xl mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">🚀 HoppX GHL Pro</h1>
            <p className="text-purple-100">Teljes értékű marketing-CRM-automatizációs platform</p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="bg-white/10 backdrop-blur-md rounded-lg px-4 py-2">
              <div className="text-white text-sm font-medium">Multi-Tenant Ready</div>
              <div className="text-purple-200 text-xs">GDPR Kompatibilis</div>
            </div>
          </div>
        </div>
      </div>

      {/* Module Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-8">
        {modules.map((module) => (
          <button
            key={module.id}
            onClick={() => setActiveModule(module.id)}
            className={`
              relative group p-4 rounded-xl transition-all duration-300 transform hover:scale-105
              bg-gradient-to-br ${module.color}
              ${activeModule === module.id ? 'ring-2 ring-white shadow-2xl scale-105' : 'hover:shadow-xl'}
            `}
          >
            <div className="text-center">
              <div className="text-3xl mb-2">{module.icon}</div>
              <h3 className="text-white font-bold text-sm mb-1">{module.title}</h3>
              <p className="text-white/80 text-xs">{module.description}</p>
            </div>
            
            {/* Active indicator */}
            {activeModule === module.id && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Module Content */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        {activeModule === 'dashboard' && <DashboardModule />}
        {activeModule === 'conversations' && <ConversationsModule />}
        {activeModule === 'calendars' && <CalendarsModule />}
        {activeModule === 'contacts' &&
          <ContactsModule 
            onCreateContact={() => {
              setSelectedContact(null);
              setShowContactModal(true);
            }}
            onEditContact={(contact) => {
              setSelectedContact(contact);
              setShowContactModal(true);
            }}
          />
        }
        {activeModule === 'opportunities' && 
          <PipelinesModule 
            onCreateOpportunity={() => {
              setSelectedOpportunity(null);
              setShowOpportunityModal(true);
            }}
            onEditOpportunity={(opportunity) => {
              setSelectedOpportunity(opportunity);
              setShowOpportunityModal(true);
            }}
            onCreatePipeline={() => {
              setSelectedPipeline(null);
              setShowPipelineModal(true);
            }}
            onEditPipeline={(pipeline) => {
              setSelectedPipeline(pipeline);
              setShowPipelineModal(true);
            }}
          />
        }
        {activeModule === 'leads' && <LeadManagementDashboard />}
        {activeModule === 'payments' && <PaymentsModule />}
        {activeModule === 'marketing' && <CampaignsModule />}
        {activeModule === 'automations' && <AutomationsModule />}
        {activeModule === 'sites' && <SitesModule />}
        {activeModule === 'reputation' && <ReputationModule />}
        {activeModule === 'reporting' && <ReportingModule />}
        {activeModule === 'settings' && <SettingsModule />}
      </div>
    </div>
  );
};

// Dashboard Module
const DashboardModule = () => {
  const [stats, setStats] = useState({
    totalContacts: 0,
    activeDeals: 0,
    automationsRunning: 0,
    monthlyRevenue: 0,
    emailsSent: 0,
    conversationRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRealStats = async () => {
      try {
        setLoading(true);
        
        // Get real user count from Firestore
        const usersQuery = query(collection(db, 'users'));
        const usersSnapshot = await getDocs(usersQuery);
        const totalUsers = usersSnapshot.size;

        // Get affiliates count
        const affiliatesQuery = query(collection(db, 'affiliates'));
        const affiliatesSnapshot = await getDocs(affiliatesQuery);
        const totalAffiliates = affiliatesSnapshot.size;

        // Get offers count
        const offersQuery = query(collection(db, 'opportunities'));
        const offersSnapshot = await getDocs(offersQuery);
        const totalOffers = offersSnapshot.size;

        // Get CRM contacts count
        const crmContactsQuery = query(collection(db, COLLECTIONS.CONTACTS));
        const crmContactsSnapshot = await getDocs(crmContactsQuery);
        const crmContactsCount = crmContactsSnapshot.size;

        // Get CRM leads count
        const crmLeadsQuery = query(collection(db, COLLECTIONS.LEADS));
        const crmLeadsSnapshot = await getDocs(crmLeadsQuery);
        const crmLeadsCount = crmLeadsSnapshot.size;

        // Get CRM opportunities count
        const crmOpportunitiesQuery = query(collection(db, COLLECTIONS.OPPORTUNITIES));
        const crmOpportunitiesSnapshot = await getDocs(crmOpportunitiesQuery);

        // Calculate total revenue from opportunities
        let totalRevenue = 0;
        if (offersSnapshot.size > 0) {
          offersSnapshot.forEach((doc) => {
            const offerData = doc.data();
            totalRevenue += offerData.monetaryValue || offerData.value || 2500000;
          });
        }

        // Calculate CRM revenue from opportunities
        let crmRevenue = 0;
        crmOpportunitiesSnapshot.forEach((doc) => {
          const oppData = doc.data();
          crmRevenue += oppData.value || 0;
        });

        // Get offers from offers collection for additional revenue calculation
        const realOffersQuery = query(collection(db, COLLECTIONS.OFFERS));
        const realOffersSnapshot = await getDocs(realOffersQuery);
        let offersRevenue = 0;
        
        realOffersSnapshot.forEach((doc) => {
          const offerData = doc.data();
          offersRevenue += offerData.totalPrice || 2500000;
        });

        const totalContactsCount = totalUsers + totalAffiliates + crmContactsCount;
        const totalDealsCount = totalOffers + crmOpportunitiesSnapshot.size + realOffersSnapshot.size;
        const combinedRevenue = totalRevenue + crmRevenue + offersRevenue;

        setStats({
          totalContacts: totalContactsCount,
          activeDeals: totalDealsCount,
          automationsRunning: 5, // CRM automations + Firebase Functions
          monthlyRevenue: combinedRevenue,
          emailsSent: totalContactsCount * 2,
          conversationRate: totalDealsCount > 0 ? 
            ((totalDealsCount / totalContactsCount) * 100).toFixed(1) : 0
        });
        
        console.log(`📊 Complete CRM Stats: ${totalContactsCount} contacts (${crmContactsCount} CRM, ${totalUsers} users, ${totalAffiliates} affiliates), ${totalDealsCount} deals, ${combinedRevenue} Ft revenue`);
        
        setLoading(false);
      } catch (error) {
        console.error('Hiba a statisztikák betöltésekor:', error);
        setLoading(false);
      }
    };

    loadRealStats();
  }, []);

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
        <span className="text-3xl mr-3">📊</span>
        Dashboard Áttekintés
      </h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Kontaktok</p>
              <p className="text-2xl font-bold">{stats.totalContacts.toLocaleString()}</p>
            </div>
            <div className="text-3xl opacity-80">👥</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Aktív Deals</p>
              <p className="text-2xl font-bold">{stats.activeDeals}</p>
            </div>
            <div className="text-3xl opacity-80">💼</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Automációk</p>
              <p className="text-2xl font-bold">{stats.automationsRunning}</p>
            </div>
            <div className="text-3xl opacity-80">⚡</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-100 text-sm">Havi bevétel</p>
              <p className="text-xl font-bold">{new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(stats.monthlyRevenue)}</p>
            </div>
            <div className="text-3xl opacity-80">💰</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-indigo-100 text-sm">Emailek</p>
              <p className="text-2xl font-bold">{stats.emailsSent.toLocaleString()}</p>
            </div>
            <div className="text-3xl opacity-80">📧</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-rose-500 to-rose-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-rose-100 text-sm">Konverzió</p>
              <p className="text-2xl font-bold">{stats.conversationRate}%</p>
            </div>
            <div className="text-3xl opacity-80">📈</div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Legutóbbi Aktivitások</h3>
        <div className="space-y-3">
          {[
            { icon: '👥', text: 'Új kontakt hozzáadva: Nagy Péter', time: '2 perce', color: 'text-green-400' },
            { icon: '💼', text: 'Deal áthelyezve: "Proposal" szakaszba', time: '5 perce', color: 'text-blue-400' },
            { icon: '📧', text: 'Email kampány elküldve: 1,250 címzett', time: '15 perce', color: 'text-purple-400' },
            { icon: '⚡', text: 'Automatizáció aktiválva: Welcome Series', time: '1 órája', color: 'text-yellow-400' }
          ].map((activity, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-[#1a1a1a] rounded-lg">
              <div className="text-2xl">{activity.icon}</div>
              <div className="flex-1">
                <p className="text-white text-sm">{activity.text}</p>
                <p className={`text-xs ${activity.color}`}>{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Comprehensive Contacts CRM Module
const ContactsModule = ({ onCreateContact, onEditContact }) => {
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  // Remove local modal states as they're now handled by parent
  const [loading, setLoading] = useState(true);

  // Load all contacts from Firebase CRM collections with real-time updates
  useEffect(() => {
    const loadContacts = async () => {
      try {
        setLoading(true);
        
        // Real-time listener for CRM contacts
        const crmContactsQuery = query(
          collection(db, COLLECTIONS.CONTACTS),
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribeCrmContacts = onSnapshot(crmContactsQuery, (snapshot) => {
          const crmContacts = [];
          snapshot.forEach((doc) => {
            const contactData = doc.data();
            crmContacts.push({
              id: doc.id,
              name: contactData.name || 'Névtelen',
              email: contactData.email || '',
              phone: contactData.phone || '',
              company: contactData.company || '',
              status: contactData.status || 'active',
              tags: contactData.tags || ['crm'],
              lastContact: contactData.lastContactDate || new Date().toISOString().split('T')[0],
              dealValue: contactData.leadScore || 0,
              source: contactData.source || 'CRM',
              assignedTo: contactData.assignedTo || 'Nem hozzárendelve',
              role: 'contact',
              createdAt: contactData.createdAt,
              lastLoginAt: contactData.lastContactDate
            });
          });
          
          // Also get users and affiliates
          Promise.all([
            getDocs(query(collection(db, COLLECTIONS.USERS), orderBy('createdAt', 'desc'))),
            getDocs(query(collection(db, COLLECTIONS.AFFILIATES), orderBy('createdAt', 'desc')))
          ]).then(([usersSnapshot, affiliatesSnapshot]) => {
            const allContacts = [...crmContacts];
            
            // Add Firebase users
            usersSnapshot.forEach((doc) => {
              const userData = doc.data();
              allContacts.push({
                id: `user_${doc.id}`,
                name: userData.name || userData.displayName || userData.email?.split('@')[0] || 'Névtelen',
                email: userData.email || '',
                phone: userData.phone || '',
                company: userData.company || '',
                status: userData.status || 'active',
                tags: [...(userData.tags || []), 'user'],
                lastContact: userData.lastLoginAt ? new Date(userData.lastLoginAt.seconds * 1000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                dealValue: userData.dealValue || 0,
                source: 'Website Registration',
                assignedTo: userData.assignedTo || 'Nem hozzárendelve',
                role: userData.role || 'user',
                createdAt: userData.createdAt,
                lastLoginAt: userData.lastLoginAt
              });
            });
            
            // Add affiliates
            affiliatesSnapshot.forEach((doc) => {
              const affiliateData = doc.data();
              allContacts.push({
                id: `affiliate_${doc.id}`,
                name: affiliateData.name || affiliateData.displayName || affiliateData.email?.split('@')[0] || 'Névtelen',
                email: affiliateData.email || '',
                phone: affiliateData.phone || '',
                company: affiliateData.company || 'Affiliate Partner',
                status: affiliateData.status || 'active',
                tags: [...(affiliateData.tags || []), 'affiliate'],
                lastContact: affiliateData.lastLoginAt ? new Date(affiliateData.lastLoginAt.seconds * 1000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                dealValue: affiliateData.totalEarnings || 0,
                source: 'Affiliate Program',
                assignedTo: affiliateData.assignedTo || 'Nem hozzárendelve',
                role: 'affiliate',
                createdAt: affiliateData.createdAt,
                lastLoginAt: affiliateData.lastLoginAt
              });
            });
            
            console.log(`📞 Loaded ${allContacts.length} contacts from Firebase CRM (${crmContacts.length} CRM contacts, ${usersSnapshot.size} users, ${affiliatesSnapshot.size} affiliates)`);
            setContacts(allContacts);
            setLoading(false);
          });
        });
        
        return () => unsubscribeCrmContacts();
        
      } catch (error) {
        console.error('Hiba a kontaktok betöltésekor:', error);
        setLoading(false);
      }
    };
    
    loadContacts();
  }, []);

  // Filter and search logic
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (contact.company || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || contact.status === filterStatus;
    const matchesTag = filterTag === 'all' || (contact.tags && contact.tags.includes(filterTag));
    
    return matchesSearch && matchesStatus && matchesTag;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'email':
        return a.email.localeCompare(b.email);
      case 'lastContact':
        return new Date(b.lastContact) - new Date(a.lastContact);
      case 'dealValue':
        return b.dealValue - a.dealValue;
      default:
        return 0;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500';
      case 'cold': return 'bg-blue-500';
      case 'inactive': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getTagColor = (tag) => {
    const colors = {
      'lead': 'bg-yellow-500',
      'customer': 'bg-green-500',
      'prospect': 'bg-blue-500',
      'vip': 'bg-purple-500',
      'recurring': 'bg-indigo-500',
      'tech': 'bg-cyan-500'
    };
    return colors[tag] || 'bg-gray-500';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">👥</span>
          Contacts (CRM)
        </h2>
        <button
          onClick={onCreateContact}
          className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Új Kontakt</span>
        </button>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-[#2a2a2a] rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Keresés név, email vagy cég alapján..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
              className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Minden címke</option>
              <option value="lead">Lead</option>
              <option value="customer">Ügyfél</option>
              <option value="prospect">Prospect</option>
              <option value="vip">VIP</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="name">Név szerint</option>
              <option value="company">Cég szerint</option>
              <option value="created">Létrehozás dátuma</option>
              <option value="value">Deal érték</option>
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-400">Kontaktok betöltése...</p>
        </div>
      ) : (
        <>
          {/* Contacts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {filteredContacts.map((contact) => (
          <div
            key={contact.id}
            className="bg-[#2a2a2a] rounded-xl p-4 hover:bg-[#3a3a3a] transition-all duration-200 cursor-pointer border border-white/5 hover:border-white/10"
            onClick={() => onEditContact(contact)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div className="text-3xl">👤</div>
                <div>
                  <h3 className="text-white font-bold text-lg">{contact.name}</h3>
                  <p className="text-gray-400 text-sm">{contact.role}</p>
                </div>
              </div>
              <div className={`w-3 h-3 rounded-full ${getStatusColor(contact.status)}`}></div>
            </div>

            <div className="space-y-2 mb-3">
              <div className="flex items-center text-gray-300 text-sm">
                <span className="mr-2">🏢</span>
                <span>{contact.company}</span>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <span className="mr-2">📧</span>
                <span className="truncate">{contact.email}</span>
              </div>
              <div className="flex items-center text-gray-300 text-sm">
                <span className="mr-2">📱</span>
                <span>{contact.phone}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-1 mb-3">
              {contact.tags.map((tag, index) => (
                <span
                  key={index}
                  className={`${getTagColor(tag)} text-white text-xs px-2 py-1 rounded-full`}
                >
                  {tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between text-sm">
              <div className="text-gray-400">
                Deal: {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(contact.dealValue)}
              </div>
              <div className="text-gray-500">
                {new Date(contact.lastContact).toLocaleDateString('hu-HU')}
              </div>
            </div>
          </div>
        ))}
      </div>


      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Összes Kontakt</p>
              <p className="text-2xl font-bold">{contacts.length}</p>
            </div>
            <div className="text-3xl opacity-80">👥</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Aktív Kontaktok</p>
              <p className="text-2xl font-bold">{contacts.filter(c => c.status === 'active').length}</p>
            </div>
            <div className="text-3xl opacity-80">✅</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">VIP Kontaktok</p>
              <p className="text-2xl font-bold">{contacts.filter(c => c.tags.includes('vip')).length}</p>
            </div>
            <div className="text-3xl opacity-80">⭐</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Összes Deal Érték</p>
              <p className="text-lg font-bold">{new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(contacts.reduce((sum, c) => sum + c.dealValue, 0))}</p>
            </div>
            <div className="text-3xl opacity-80">💰</div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

const PipelinesModule = ({ onCreateOpportunity, onEditOpportunity, onCreatePipeline, onEditPipeline }) => {
  const [selectedPipeline, setSelectedPipeline] = useState('sales');
  const [deals, setDeals] = useState({});
  const [selectedDeal, setSelectedDeal] = useState(null);
  // Remove local modal states as they're now handled by parent
  const [draggedDeal, setDraggedDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load opportunities and pipelines from Firebase CRM with real-time updates
  useEffect(() => {
    const loadOpportunities = async () => {
      try {
        setLoading(true);
        
        // Real-time listener for CRM opportunities
        const opportunitiesQuery = query(
          collection(db, COLLECTIONS.OPPORTUNITIES),
          orderBy('createdAt', 'desc')
        );
        
        const unsubscribeOpportunities = onSnapshot(opportunitiesQuery, (snapshot) => {
          const transformedDeals = {};
          
          // Add CRM opportunities
          snapshot.forEach((doc) => {
            const oppData = doc.data();
            const stageId = oppData.stageId || 'lead';
            
            if (!transformedDeals[stageId]) {
              transformedDeals[stageId] = [];
            }
            
            transformedDeals[stageId].push({
              id: doc.id,
              title: oppData.name || 'Névtelen ügylet',
              contact: oppData.contactId || 'Ismeretlen',
              email: '',
              phone: '',
              value: oppData.value || 0,
              probability: oppData.probability || 50,
              expectedClose: oppData.expectedCloseDate || new Date().toISOString().split('T')[0],
              priority: oppData.priority || 'medium',
              tags: oppData.tags || ['opportunity'],
              notes: oppData.notes?.join(', ') || '',
              source: 'CRM',
              assignedTo: oppData.assignedTo || 'Nem hozzárendelve',
              createdAt: oppData.createdAt,
              updatedAt: oppData.updatedAt,
              status: oppData.status || 'open'
            });
          });
          
          // Also get offers as additional opportunities
          getDocs(query(collection(db, COLLECTIONS.OFFERS), orderBy('createdAt', 'desc')))
            .then((offersSnapshot) => {
              offersSnapshot.forEach((doc) => {
                const offerData = doc.data();
                const stageId = offerData.status === 'completed' ? 'closed-won' : 'proposal';
                
                if (!transformedDeals[stageId]) {
                  transformedDeals[stageId] = [];
                }
                
                transformedDeals[stageId].push({
                  id: `offer_${doc.id}`,
                  title: `Mobilház ajánlat - ${offerData.name || 'Névtelen'}`,
                  contact: offerData.name || 'Ismeretlen',
                  email: offerData.email || '',
                  phone: offerData.phone || '',
                  value: offerData.totalPrice || 2500000,
                  probability: offerData.status === 'completed' ? 100 : 75,
                  expectedClose: offerData.createdAt ? new Date(offerData.createdAt.seconds * 1000 + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                  priority: 'high',
                  tags: ['offer', 'mobilház'],
                  notes: `Mobilház típus: ${offerData.houseType || 'Nem megadott'}`,
                  source: 'Website Offer',
                  assignedTo: 'Értékesítési csapat',
                  createdAt: offerData.createdAt,
                  updatedAt: offerData.updatedAt || offerData.createdAt,
                  status: offerData.status || 'pending'
                });
              });
              
              console.log(`💼 Loaded ${snapshot.size + offersSnapshot.size} deals from Firebase CRM (${snapshot.size} opportunities, ${offersSnapshot.size} offers)`);
              setDeals(transformedDeals);
              setLoading(false);
            });
        });
        
        return () => unsubscribeOpportunities();
        
      } catch (error) {
        console.error('Hiba az opportunities betöltésekor:', error);
        setLoading(false);
      }
    };
    
    loadOpportunities();
  }, [selectedPipeline]);
  // Remove old form states as they're handled by CRM modals

  // Pipeline configurations
  const pipelines = {
    sales: {
      name: 'Értékesítési Pipeline',
      stages: [
        { id: 'lead', name: 'Lead', color: 'bg-yellow-500', deals: [] },
        { id: 'qualified', name: 'Kvalifikált', color: 'bg-blue-500', deals: [] },
        { id: 'proposal', name: 'Ajánlat', color: 'bg-purple-500', deals: [] },
        { id: 'negotiation', name: 'Tárgyalás', color: 'bg-orange-500', deals: [] },
        { id: 'closed-won', name: 'Megnyert', color: 'bg-green-500', deals: [] },
        { id: 'closed-lost', name: 'Elveszett', color: 'bg-red-500', deals: [] }
      ]
    },
    support: {
      name: 'Ügyfélszolgálat Pipeline',
      stages: [
        { id: 'new', name: 'Új', color: 'bg-blue-500', deals: [] },
        { id: 'in-progress', name: 'Folyamatban', color: 'bg-yellow-500', deals: [] },
        { id: 'waiting', name: 'Várakozik', color: 'bg-orange-500', deals: [] },
        { id: 'resolved', name: 'Megoldva', color: 'bg-green-500', deals: [] }
      ]
    }
  };

  useEffect(() => {
    const loadRealDeals = async () => {
      try {
        setLoading(true);
        
        // Get real offers from Firestore
        const offersQuery = query(collection(db, 'offers'), orderBy('createdAt', 'desc'));
        const offersSnapshot = await getDocs(offersQuery);
        
        // Get real users from Firestore
        const usersQuery = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
        const usersSnapshot = await getDocs(usersQuery);
        
        // Get real affiliates from Firestore
        const affiliatesQuery = query(collection(db, 'affiliates'), orderBy('createdAt', 'desc'));
        const affiliatesSnapshot = await getDocs(affiliatesQuery);

        const realDeals = {
          sales: {
            lead: [],
            qualified: [],
            proposal: [],
            negotiation: [],
            'closed-won': [],
            'closed-lost': []
          }
        };

        // Convert offers to deals
        offersSnapshot.forEach((doc) => {
          const offer = doc.data();
          const deal = {
            id: doc.id,
            title: `${offer.name} - Mobilház ajánlat`,
            contact: offer.name,
            value: offer.interestedInTurnkey ? 15000000 : 8000000, // Estimated value
            probability: 50,
            expectedClose: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            created: offer.createdAt?.toDate?.()?.toISOString?.()?.split('T')[0] || new Date().toISOString().split('T')[0],
            priority: offer.hasPlot ? 'high' : 'medium',
            tags: [offer.hasPlot ? 'has-plot' : 'no-plot', offer.interestedInTurnkey ? 'turnkey' : 'basic'],
            phone: offer.phone,
            email: offer.email
          };
          realDeals.sales.lead.push(deal);
        });

        // Convert users to potential deals
        usersSnapshot.forEach((doc) => {
          const user = doc.data();
          if (user.role === 'user') {
            const deal = {
              id: `user-${doc.id}`,
              title: `${user.name || user.email} - Potenciális ügyfél`,
              contact: user.name || user.email,
              value: 5000000, // Estimated value
              probability: 20,
              expectedClose: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              created: user.createdAt?.toDate?.()?.toISOString?.()?.split('T')[0] || new Date().toISOString().split('T')[0],
              priority: 'low',
              tags: ['potential', 'user'],
              phone: user.phone,
              email: user.email
            };
            realDeals.sales.qualified.push(deal);
          }
        });

        // Convert affiliates to deals
        affiliatesSnapshot.forEach((doc) => {
          const affiliate = doc.data();
          const deal = {
            id: `affiliate-${doc.id}`,
            title: `${affiliate.name} - Partner ajánlat`,
            contact: affiliate.name,
            value: 3000000, // Commission potential
            probability: 70,
            expectedClose: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            created: affiliate.createdAt?.toDate?.()?.toISOString?.()?.split('T')[0] || new Date().toISOString().split('T')[0],
            priority: 'high',
            tags: ['affiliate', 'partner'],
            phone: affiliate.phone,
            email: affiliate.email
          };
          realDeals.sales.proposal.push(deal);
        });

        setDeals(realDeals);
        setLoading(false);
      } catch (error) {
        console.error('Hiba a deals betöltésekor:', error);
        setLoading(false);
      }
    };

    loadRealDeals();
  }, []);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const getStageColor = (stageId) => {
    switch (stageId) {
      case 'lead': return 'border-l-yellow-500';
      case 'qualified': return 'border-l-blue-500';
      case 'proposal': return 'border-l-purple-500';
      case 'negotiation': return 'border-l-orange-500';
      case 'closed-won': return 'border-l-green-500';
      case 'closed-lost': return 'border-l-red-500';
      default: return 'border-l-gray-500';
    }
  };

  const getTagColor = (tag) => {
    const colors = {
      'enterprise': 'bg-purple-500',
      'premium': 'bg-gold-500',
      'startup': 'bg-blue-500',
      'marketing': 'bg-pink-500',
      'recurring': 'bg-indigo-500',
      'tech': 'bg-cyan-500',
      'web': 'bg-green-500',
      'standard': 'bg-gray-500',
      'technical': 'bg-red-500',
      'email': 'bg-orange-500',
      'automation': 'bg-purple-500',
      'setup': 'bg-yellow-500'
    };
    return colors[tag] || 'bg-gray-500';
  };

  const handleDragStart = (e, deal, stageId) => {
    setDraggedDeal({ deal, sourceStage: stageId });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStageId) => {
    e.preventDefault();
    
    if (!draggedDeal || draggedDeal.sourceStage === targetStageId) {
      setDraggedDeal(null);
      return;
    }

    try {
      const { deal, sourceStage } = draggedDeal;
      
      // Update deal in Firestore with new stage
      const dealRef = doc(db, 'deals', deal.id);
      await updateDoc(dealRef, {
        stage: targetStageId,
        updatedAt: new Date()
      });

      // Update local state
      setDeals(prev => {
        const newDeals = { ...prev };
        const pipeline = newDeals[selectedPipeline];
        
        // Remove from source stage
        pipeline[sourceStage] = pipeline[sourceStage].filter(d => d.id !== deal.id);
        
        // Add to target stage
        const updatedDeal = { ...deal, stage: targetStageId };
        pipeline[targetStageId] = [...(pipeline[targetStageId] || []), updatedDeal];
        
        return newDeals;
      });

      setDraggedDeal(null);
    } catch (error) {
      console.error('Hiba a deal mozgatásakor:', error);
      setDraggedDeal(null);
    }
  };


  const currentPipeline = pipelines[selectedPipeline];
  const currentDeals = deals[selectedPipeline] || {};

  // Calculate pipeline statistics
  const totalValue = Object.values(currentDeals).flat().reduce((sum, deal) => sum + deal.value, 0);
  const totalDeals = Object.values(currentDeals).flat().length;
  const wonDeals = currentDeals['closed-won'] ? currentDeals['closed-won'].length : 0;
  const winRate = totalDeals > 0 ? ((wonDeals / totalDeals) * 100).toFixed(1) : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">🔄</span>
          Pipelines & Deals
        </h2>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPipeline}
            onChange={(e) => setSelectedPipeline(e.target.value)}
            className="bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {Object.entries(pipelines).map(([key, pipeline]) => (
              <option key={key} value={key}>{pipeline.name}</option>
            ))}
          </select>
          <button
            onClick={onCreatePipeline}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
          >
            <span>🔧</span>
            <span>Új Pipeline</span>
          </button>
          <button
            onClick={() => {
              onEditPipeline(currentPipeline);
            }}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
          >
            <span>✏️</span>
            <span>Pipeline Szerkesztése</span>
          </button>
          <button
            onClick={onCreateOpportunity}
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Új Deal</span>
          </button>
        </div>
      </div>

      {/* Pipeline Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Összes Deal</p>
              <p className="text-2xl font-bold">{totalDeals}</p>
            </div>
            <div className="text-3xl opacity-80">📊</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Pipeline Érték</p>
              <p className="text-lg font-bold">{new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(totalValue)}</p>
            </div>
            <div className="text-3xl opacity-80">💰</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Nyerési arány</p>
              <p className="text-2xl font-bold">{winRate}%</p>
            </div>
            <div className="text-3xl opacity-80">🎯</div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm">Átlagos Deal</p>
              <p className="text-lg font-bold">{totalDeals > 0 ? new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(totalValue / totalDeals) : '0 Ft'}</p>
            </div>
            <div className="text-3xl opacity-80">📈</div>
          </div>
        </div>
      </div>

      {/* Kanban Pipeline Board */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        <div className="flex space-x-4 overflow-x-auto pb-4">
          {currentPipeline.stages.map((stage) => {
            const stageDeals = currentDeals[stage.id] || [];
            const stageValue = stageDeals.reduce((sum, deal) => sum + deal.value, 0);
            
            return (
              <div
                key={stage.id}
                className="flex-shrink-0 w-80 bg-[#2a2a2a] rounded-xl border border-white/5"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                <div className={`${stage.color} rounded-t-xl p-4 text-white`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg">{stage.name}</h3>
                    <div className="bg-white/20 rounded-full px-2 py-1 text-sm font-medium">
                      {stageDeals.length}
                    </div>
                  </div>
                  {selectedPipeline === 'sales' && (
                    <p className="text-sm opacity-90 mt-1">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(stageValue)}
                    </p>
                  )}
                </div>

                <div className="p-4 space-y-3 min-h-[400px]">
                  {stageDeals.map((deal) => (
                    <div
                      key={deal.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, deal, stage.id)}
                      onClick={() => onEditOpportunity(deal)}
                      className={`bg-[#1a1a1a] rounded-lg p-4 border-l-4 ${getPriorityColor(deal.priority)} hover:bg-[#3a3a3a] transition-all duration-200 cursor-pointer hover:shadow-lg`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-white font-medium text-sm leading-tight">{deal.title}</h4>
                        <div className={`w-2 h-2 rounded-full ${deal.priority === 'high' ? 'bg-red-500' : deal.priority === 'medium' ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
                      </div>

                      <div className="text-gray-400 text-xs mb-2">
                        👤 {deal.contact}
                      </div>

                      {selectedPipeline === 'sales' && deal.value > 0 && (
                        <div className="text-green-400 font-bold text-sm mb-2">
                          {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(deal.value)}
                        </div>
                      )}

                      {selectedPipeline === 'sales' && (
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                          <span>📊 {deal.probability}%</span>
                          <span>📅 {new Date(deal.expectedClose).toLocaleDateString('hu-HU')}</span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-1">
                        {deal.tags.map((tag, index) => (
                          <span
                            key={index}
                            className={`${getTagColor(tag)} text-white text-xs px-2 py-1 rounded-full`}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Deal Detail Modal */}
      {selectedDeal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1a1a] rounded-xl border border-white/10 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white">{selectedDeal.title}</h2>
                  <p className="text-gray-400">Kontakt: {selectedDeal.contact}</p>
                </div>
                <button
                  onClick={() => setSelectedDeal(null)}
                  className="text-gray-400 hover:text-white text-2xl"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-white font-bold mb-2">Deal Információk</h3>
                    <div className="space-y-2">
                      {selectedPipeline === 'sales' && (
                        <>
                          <div className="flex justify-between text-gray-300">
                            <span>Érték:</span>
                            <span className="text-green-400 font-bold">{new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF', minimumFractionDigits: 0 }).format(selectedDeal.value)}</span>
                          </div>
                          <div className="flex justify-between text-gray-300">
                            <span>Valószínűség:</span>
                            <span>{selectedDeal.probability}%</span>
                          </div>
                          <div className="flex justify-between text-gray-300">
                            <span>Várható lezárás:</span>
                            <span>{new Date(selectedDeal.expectedClose).toLocaleDateString('hu-HU')}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between text-gray-300">
                        <span>Prioritás:</span>
                        <span className={`capitalize ${selectedDeal.priority === 'high' ? 'text-red-400' : selectedDeal.priority === 'medium' ? 'text-yellow-400' : 'text-green-400'}`}>
                          {selectedDeal.priority}
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-300">
                        <span>Létrehozva:</span>
                        <span>{new Date(selectedDeal.created).toLocaleDateString('hu-HU')}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-white font-bold mb-2">Címkék</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedDeal.tags.map((tag, index) => (
                        <span
                          key={index}
                          className={`${getTagColor(tag)} text-white text-sm px-3 py-1 rounded-full`}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <h3 className="text-white font-bold mb-3">Aktivitás Timeline</h3>
                    <div className="space-y-3">
                      {[
                        { icon: '📧', text: 'Ajánlat elküldve', date: '2024-01-20', type: 'email' },
                        { icon: '📞', text: 'Követő hívás', date: '2024-01-18', type: 'call' },
                        { icon: '📝', text: 'Deal létrehozva', date: selectedDeal.created, type: 'created' }
                      ].map((activity, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-[#2a2a2a] rounded-lg">
                          <div className="text-xl">{activity.icon}</div>
                          <div className="flex-1">
                            <p className="text-white text-sm">{activity.text}</p>
                            <p className="text-gray-400 text-xs">{new Date(activity.date).toLocaleDateString('hu-HU')}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={async () => {
                    if (confirm('Biztosan törölni szeretnéd ezt a deal-t? Ez a művelet nem visszavonható.')) {
                      try {
                        // Delete from Firestore
                        await deleteDoc(doc(db, 'deals', selectedDeal.id));
                        
                        // Update local state
                        setDeals(prev => {
                          const newDeals = { ...prev };
                          const pipeline = newDeals[selectedPipeline];
                          
                          // Find and remove deal from all stages
                          Object.keys(pipeline).forEach(stageId => {
                            pipeline[stageId] = pipeline[stageId].filter(d => d.id !== selectedDeal.id);
                          });
                          
                          return newDeals;
                        });

                        setSelectedDeal(null);
                      } catch (error) {
                        console.error('Hiba a deal törlésekor:', error);
                      }
                    }
                  }}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Deal Törlése
                </button>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      // Pre-fill the deal form with current deal data for editing
                      onEditOpportunity(selectedDeal);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Deal Szerkesztése
                  </button>
                  <button
                    onClick={() => setSelectedDeal(null)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Bezárás
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}



    </div>
  );
};

const AutomationsModule = () => {
  const [automations, setAutomations] = useState([]);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('triggers');
  const [workflowNodes, setWorkflowNodes] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [triggers, setTriggers] = useState([]);
  const [draggedTrigger, setDraggedTrigger] = useState(null);

  // Trigger templates
  const triggerTemplates = [
    {
      id: 'contact_created',
      name: 'Új kontakt hozzáadása pipeline-hoz - FINAL',
      description: 'Aktiválódik amikor új kontakt kerül a pipeline-ba',
      status: 'draft',
      icon: '👤',
      category: 'contact'
    },
    {
      id: 'lead_from_external',
      name: 'Lead külső forrásból (FINAL)',
      description: 'Lead érkezik külső forrásból',
      status: 'draft', 
      icon: '📥',
      category: 'lead'
    },
    {
      id: 'responsive_lead_sms',
      name: 'Responsive Lead - SMS',
      description: 'SMS küldés responsive lead esetén',
      status: 'draft',
      icon: '📱',
      category: 'sms'
    },
    {
      id: 'responsive_lead_call',
      name: 'Responsive Lead - Call',
      description: 'Hívás indítás responsive lead esetén',
      status: 'draft',
      icon: '📞',
      category: 'call'
    },
    {
      id: 'not_booked_stage_change',
      name: 'Not booked - change stage and remove lead nurture',
      description: 'Státusz változtatás és lead nurture eltávolítása',
      status: 'draft',
      icon: '🔄',
      category: 'stage'
    },
    {
      id: 'no_show_final',
      name: 'No Show (FINAL)',
      description: 'Nem jelent meg esemény kezelése',
      status: 'draft',
      icon: '❌',
      category: 'event'
    },
    {
      id: 'appt_cancelled_final',
      name: 'Appt Cancelled (FINAL)',
      description: 'Időpont lemondás kezelése',
      status: 'draft',
      icon: '🚫',
      category: 'appointment'
    }
  ];

  // Sample automations data
  const sampleAutomations = [
    {
      id: '1',
      name: 'Welcome Series',
      description: 'Új kontaktok számára üdvözlő email sorozat',
      status: 'active',
      trigger: 'form_submission',
      created: '2024-01-10',
      lastRun: '2024-01-22',
      totalRuns: 1247,
      successRate: 94.2,
      tags: ['onboarding', 'email'],
      nodes: [
        { id: 'trigger-1', type: 'trigger', title: 'Form Submission', x: 100, y: 100 },
        { id: 'delay-1', type: 'delay', title: 'Wait 1 hour', x: 300, y: 100 },
        { id: 'email-1', type: 'email', title: 'Welcome Email', x: 500, y: 100 },
        { id: 'delay-2', type: 'delay', title: 'Wait 3 days', x: 700, y: 100 },
        { id: 'email-2', type: 'email', title: 'Getting Started', x: 900, y: 100 }
      ]
    },
    {
      id: '2',
      name: 'Lead Nurturing',
      description: 'Leadek ápolása személyre szabott tartalommal',
      status: 'active',
      trigger: 'tag_added',
      created: '2024-01-05',
      lastRun: '2024-01-21',
      totalRuns: 856,
      successRate: 87.5,
      tags: ['nurturing', 'sales'],
      nodes: [
        { id: 'trigger-2', type: 'trigger', title: 'Tag Added: Lead', x: 100, y: 100 },
        { id: 'condition-1', type: 'condition', title: 'Industry Check', x: 300, y: 100 },
        { id: 'email-3', type: 'email', title: 'Tech Industry Email', x: 500, y: 50 },
        { id: 'email-4', type: 'email', title: 'General Email', x: 500, y: 150 }
      ]
    },
    {
      id: '3',
      name: 'Abandoned Cart Recovery',
      description: 'Elhagyott kosár visszanyerés kampány',
      status: 'paused',
      trigger: 'cart_abandoned',
      created: '2024-01-15',
      lastRun: '2024-01-20',
      totalRuns: 432,
      successRate: 76.8,
      tags: ['ecommerce', 'recovery'],
      nodes: []
    }
  ];

  // Node types configuration
  const nodeTypes = {
    trigger: {
      icon: '🎯',
      color: 'bg-green-500',
      category: 'Triggers',
      options: ['Form Submission', 'Tag Added', 'Pipeline Stage', 'Date/Time', 'Webhook']
    },
    condition: {
      icon: '❓',
      color: 'bg-yellow-500',
      category: 'Logic',
      options: ['If/Then', 'A/B Split', 'Wait Until', 'Filter']
    },
    action: {
      icon: '⚡',
      color: 'bg-blue-500',
      category: 'Actions',
      options: ['Send Email', 'Send SMS', 'Add Tag', 'Create Task', 'Webhook']
    },
    delay: {
      icon: '⏰',
      color: 'bg-purple-500',
      category: 'Timing',
      options: ['Wait', 'Schedule', 'Delay Until']
    },
    email: {
      icon: '📧',
      color: 'bg-indigo-500',
      category: 'Communication',
      options: ['Email Template', 'Dynamic Email', 'Personalized Email']
    }
  };

  useEffect(() => {
    loadAutomations();
    setTriggers(triggerTemplates);
  }, []);

  const loadAutomations = async () => {
    try {
      setAutomations(sampleAutomations);
    } catch (error) {
      console.error('Hiba az automations betöltésekor:', error);
      setAutomations(sampleAutomations);
    }
  };

  const handleDragStart = (e, trigger) => {
    setDraggedTrigger(trigger);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (draggedTrigger) {
      // Create new automation from trigger
      const newAutomation = {
        id: Date.now().toString(),
        name: draggedTrigger.name,
        description: draggedTrigger.description,
        status: 'draft',
        trigger: draggedTrigger.id,
        actions: 0,
        conversions: 0,
        created: new Date().toISOString().split('T')[0],
        tags: [draggedTrigger.category]
      };
      setAutomations(prev => [...prev, newAutomation]);
      setDraggedTrigger(null);
    }
  };

  const TriggersTab = () => (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Trigger Templates */}
      <div className="lg:col-span-1">
        <div className="bg-[#2a2a2a] rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-bold">Triggerek</h3>
            <div className="flex space-x-2">
              <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm">
                + Új Mappa
              </button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">
                + Trigger Hozzáadása
              </button>
            </div>
          </div>
          
          <div className="text-sm text-gray-400 mb-4">
            Megjelenítés: <span className="text-blue-400">Mind</span> szabályok alapján <span className="text-blue-400">Bármely Feltétel</span> ami tűz <span className="text-blue-400">Bármely Művelet</span>
          </div>

          <div className="space-y-2">
            {triggers.map((trigger) => (
              <div
                key={trigger.id}
                draggable
                onDragStart={(e) => handleDragStart(e, trigger)}
                className="bg-[#1a1a1a] rounded-lg p-3 cursor-move hover:bg-[#3a3a3a] transition-colors border border-white/10"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-lg">{trigger.icon}</span>
                    <div>
                      <div className="text-white font-medium text-sm">{trigger.name}</div>
                      <div className="text-gray-400 text-xs">{trigger.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs ${
                      trigger.status === 'draft' ? 'bg-yellow-500/20 text-yellow-300' : 'bg-green-500/20 text-green-300'
                    }`}>
                      {trigger.status === 'draft' ? 'Vázlat' : 'Aktív'}
                    </span>
                    <button className="text-blue-400 hover:text-blue-300 text-sm">
                      Szerkesztés
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Workflow Canvas */}
      <div className="lg:col-span-2">
        <div 
          className="bg-[#1a1a1a] rounded-xl border-2 border-dashed border-white/[0.08] p-8 min-h-[600px] relative"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="text-center text-gray-400">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-bold mb-2">Húzd ide a triggereket</h3>
            <p>Húzz egy triggert a bal oldalról ide a workflow létrehozásához</p>
          </div>
          
          {draggedTrigger && (
            <div className="absolute inset-0 bg-blue-500/10 border-2 border-blue-500 border-dashed rounded-xl flex items-center justify-center">
              <div className="text-blue-400 text-lg font-medium">
                Engedd el a triggert itt
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">🤖</span>
          Automatizálás
        </h2>
        <div className="flex space-x-2">
          <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200">
            Importálás
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
          >
            <span>➕</span>
            <span>Új Automation</span>
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-[#2a2a2a] rounded-lg p-1">
        {[
          { id: 'triggers', label: 'Triggerek', icon: '🎯' },
          { id: 'campaigns', label: 'Kampányok', icon: '📧' },
          { id: 'workflows', label: 'Workflow', icon: '🔄' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a]'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        {activeTab === 'triggers' && <TriggersTab />}
        
        {activeTab === 'campaigns' && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📧</div>
            <h3 className="text-2xl font-bold text-white mb-2">Kampányok</h3>
            <p className="text-gray-400">Email és SMS kampányok kezelése</p>
          </div>
        )}

        {activeTab === 'workflows' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {automations.map((automation) => (
              <div
                key={automation.id}
                onClick={() => setSelectedAutomation(automation)}
                className="bg-[#2a2a2a] rounded-xl p-6 hover:bg-[#3a3a3a] transition-all duration-200 cursor-pointer border border-white/5 hover:border-white/10"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="bg-purple-500 rounded-lg p-3 text-white">
                    <div className="text-2xl">🤖</div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    automation.status === 'active' ? 'bg-green-500/20 text-green-300' :
                    automation.status === 'paused' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-gray-500/20 text-gray-300'
                  }`}>
                    {automation.status === 'active' ? 'Aktív' :
                     automation.status === 'paused' ? 'Szüneteltetve' : 'Vázlat'}
                  </div>
                </div>

                <h4 className="text-white font-bold text-lg mb-2">{automation.name}</h4>
                <p className="text-gray-400 text-sm mb-4">{automation.description}</p>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-white">{automation.actions || 0}</div>
                    <div className="text-xs text-gray-400">Műveletek</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-400">{automation.conversions || 0}</div>
                    <div className="text-xs text-gray-400">Konverziók</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1 mb-4">
                  {automation.tags && automation.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-blue-500/20 text-blue-300 text-xs px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="text-gray-400 text-xs">
                  Létrehozva: {automation.created}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const CalendarsModule = () => {
  const [activeTab, setActiveTab] = useState('calendar');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [viewMode, setViewMode] = useState('week'); // week, month, day

  // Sample appointments data
  const sampleAppointments = [
    {
      id: '1',
      title: 'Ingatlan konzultáció - Nagy Péter',
      client: 'Nagy Péter',
      email: 'peter.nagy@email.com',
      phone: '+36 30 123 4567',
      type: 'consultation',
      date: '2024-01-22',
      startTime: '10:00',
      endTime: '11:00',
      status: 'confirmed',
      location: 'Iroda - 1. terem',
      notes: 'Családi ház értékelés, Budán',
      reminders: ['email-24h', 'sms-1h']
    },
    {
      id: '2',
      title: 'Helyszíni felmérés - Kovács Anna',
      client: 'Kovács Anna',
      email: 'anna.kovacs@company.hu',
      phone: '+36 70 987 6543',
      type: 'site_visit',
      date: '2024-01-22',
      startTime: '14:30',
      endTime: '16:00',
      status: 'pending',
      location: '1234 Budapest, Példa utca 12.',
      notes: 'Lakás felújítás konzultáció',
      reminders: ['email-24h']
    },
    {
      id: '3',
      title: 'Online bemutató - Szabó Gábor',
      client: 'Szabó Gábor',
      email: 'gabor.szabo@startup.com',
      phone: '+36 20 555 1234',
      type: 'online_demo',
      date: '2024-01-23',
      startTime: '09:00',
      endTime: '09:30',
      status: 'confirmed',
      location: 'Zoom Meeting',
      notes: 'Premium csomag bemutató',
      reminders: ['email-24h', 'email-1h']
    }
  ];

  // Available time slots
  const timeSlots = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    '17:00', '17:30', '18:00'
  ];

  // Appointment types
  const appointmentTypes = [
    {
      id: 'consultation',
      name: 'Konzultáció',
      duration: 60,
      color: 'bg-blue-500',
      icon: '💼',
      description: 'Általános tanácsadás'
    },
    {
      id: 'site_visit',
      name: 'Helyszíni felmérés',
      duration: 90,
      color: 'bg-green-500',
      icon: '🏠',
      description: 'Ingatlan megtekintés'
    },
    {
      id: 'online_demo',
      name: 'Online bemutató',
      duration: 30,
      color: 'bg-purple-500',
      icon: '💻',
      description: 'Virtuális prezentáció'
    },
    {
      id: 'follow_up',
      name: 'Utánkövetés',
      duration: 30,
      color: 'bg-yellow-500',
      icon: '📞',
      description: 'Telefonos egyeztetés'
    }
  ];

  useEffect(() => {
    setAppointments(sampleAppointments);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'cancelled': return 'bg-red-500';
      case 'completed': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getAppointmentTypeColor = (type) => {
    const appointmentType = appointmentTypes.find(t => t.id === type);
    return appointmentType ? appointmentType.color : 'bg-gray-500';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time) => {
    return time;
  };

  const getDayAppointments = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(apt => apt.date === dateStr);
  };

  const getWeekDays = (date) => {
    const week = [];
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
    startOfWeek.setDate(diff);

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const BookingModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#2a2a2a] rounded-xl p-6 w-full max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Új időpont foglalása</h2>
          <button
            onClick={() => setShowBookingModal(false)}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white font-medium mb-2">Ügyfél neve</label>
              <input
                type="text"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Teljes név"
              />
            </div>
            <div>
              <label className="block text-white font-medium mb-2">Email cím</label>
              <input
                type="email"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email@example.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white font-medium mb-2">Telefonszám</label>
              <input
                type="tel"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="+36 30 123 4567"
              />
            </div>
            <div>
              <label className="block text-white font-medium mb-2">Időpont típusa</label>
              <select className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                {appointmentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.icon} {type.name} ({type.duration} perc)
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-white font-medium mb-2">Dátum</label>
              <input
                type="date"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-white font-medium mb-2">Időpont</label>
              <select className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                {timeSlots.map((time) => (
                  <option key={time} value={time}>{time}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-white font-medium mb-2">Helyszín</label>
            <input
              type="text"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Iroda, online, vagy helyszín címe"
            />
          </div>

          <div>
            <label className="block text-white font-medium mb-2">Megjegyzések</label>
            <textarea
              rows="3"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="További információk..."
            />
          </div>

          <div>
            <label className="block text-white font-medium mb-2">Emlékeztetők</label>
            <div className="space-y-2">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="text-blue-600" defaultChecked />
                <span className="text-gray-300">Email 24 órával korábban</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="text-blue-600" />
                <span className="text-gray-300">SMS 1 órával korábban</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="text-blue-600" />
                <span className="text-gray-300">Email 1 órával korábban</span>
              </label>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-white/10">
          <button
            onClick={() => setShowBookingModal(false)}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Mégse
          </button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
            Időpont foglalása
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <span className="text-3xl mr-3">📅</span>
            Calendars & Booking
          </h2>
          <p className="text-gray-400 mt-1">Időpontfoglalás és naptár kezelés</p>
        </div>
        <button
          onClick={() => setShowBookingModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
        >
          <span className="mr-2">+</span>
          Új időpont
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-[#2a2a2a] rounded-lg p-1">
        {[
          { id: 'calendar', label: 'Naptár', icon: '📅' },
          { id: 'appointments', label: 'Időpontok', icon: '📋' },
          { id: 'availability', label: 'Elérhetőség', icon: '⏰' },
          { id: 'settings', label: 'Beállítások', icon: '⚙️' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a]'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          {/* Calendar Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(selectedDate.getDate() - 7);
                  setSelectedDate(newDate);
                }}
                className="text-gray-400 hover:text-white"
              >
                ← Előző hét
              </button>
              <h3 className="text-xl font-bold text-white">
                {formatDate(selectedDate)}
              </h3>
              <button
                onClick={() => {
                  const newDate = new Date(selectedDate);
                  newDate.setDate(selectedDate.getDate() + 7);
                  setSelectedDate(newDate);
                }}
                className="text-gray-400 hover:text-white"
              >
                Következő hét →
              </button>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedDate(new Date())}
                className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
              >
                Ma
              </button>
            </div>
          </div>

          {/* Weekly Calendar View */}
          <div className="bg-[#2a2a2a] rounded-xl overflow-hidden">
            {/* Week Header */}
            <div className="grid grid-cols-8 border-b border-white/10">
              <div className="p-3 text-gray-400 text-sm font-medium">Idő</div>
              {getWeekDays(selectedDate).map((day, index) => (
                <div key={index} className="p-3 text-center border-l border-white/10">
                  <div className="text-white font-medium">
                    {day.toLocaleDateString('hu-HU', { weekday: 'short' })}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {day.getDate()}
                  </div>
                </div>
              ))}
            </div>

            {/* Time Slots */}
            <div className="max-h-96 overflow-y-auto">
              {timeSlots.map((time) => (
                <div key={time} className="grid grid-cols-8 border-b border-white/5">
                  <div className="p-2 text-gray-400 text-sm border-r border-white/10">
                    {time}
                  </div>
                  {getWeekDays(selectedDate).map((day, dayIndex) => {
                    const dayAppointments = getDayAppointments(day);
                    const timeAppointment = dayAppointments.find(apt => apt.startTime === time);
                    
                    return (
                      <div
                        key={dayIndex}
                        className="p-1 border-l border-white/5 min-h-[40px] hover:bg-[#3a3a3a] cursor-pointer transition-colors"
                        onClick={() => setSelectedTimeSlot({ date: day, time })}
                      >
                        {timeAppointment && (
                          <div className={`${getAppointmentTypeColor(timeAppointment.type)} rounded p-1 text-white text-xs`}>
                            <div className="font-medium truncate">{timeAppointment.client}</div>
                            <div className="truncate">{timeAppointment.title}</div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'appointments' && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-white">Időpontok kezelése</h3>
          
          {/* Appointments List */}
          <div className="space-y-4">
            {appointments.map((appointment) => (
              <div key={appointment.id} className="bg-[#2a2a2a] rounded-xl p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className={`w-4 h-4 rounded-full ${getAppointmentTypeColor(appointment.type)} mt-1`}></div>
                    <div className="flex-1">
                      <h4 className="text-white font-bold text-lg">{appointment.title}</h4>
                      <p className="text-gray-400 text-sm mb-2">{appointment.client}</p>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-400">Dátum:</span>
                          <span className="text-white ml-2">{formatDate(appointment.date)}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Idő:</span>
                          <span className="text-white ml-2">{appointment.startTime} - {appointment.endTime}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Helyszín:</span>
                          <span className="text-white ml-2">{appointment.location}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Telefon:</span>
                          <span className="text-white ml-2">{appointment.phone}</span>
                        </div>
                      </div>
                      
                      {appointment.notes && (
                        <div className="mt-3">
                          <span className="text-gray-400 text-sm">Megjegyzések:</span>
                          <p className="text-gray-300 text-sm mt-1">{appointment.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(appointment.status)}`}></div>
                    <span className="text-gray-300 text-sm capitalize">{appointment.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'availability' && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-white">Elérhetőségi beállítások</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Working Hours */}
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-4">Munkaidő</h4>
              <div className="space-y-4">
                {['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek', 'Szombat', 'Vasárnap'].map((day) => (
                  <div key={day} className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <input type="checkbox" className="text-blue-600" defaultChecked={day !== 'Vasárnap'} />
                      <span className="text-white">{day}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="time"
                        defaultValue="09:00"
                        className="bg-[#1a1a1a] border border-white/10 rounded px-2 py-1 text-white text-sm"
                      />
                      <span className="text-gray-400">-</span>
                      <input
                        type="time"
                        defaultValue="17:00"
                        className="bg-[#1a1a1a] border border-white/10 rounded px-2 py-1 text-white text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Buffer Times */}
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-4">Buffer idők</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Időpont előtt (perc)</label>
                  <input
                    type="number"
                    defaultValue="15"
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Időpont után (perc)</label>
                  <input
                    type="number"
                    defaultValue="15"
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Minimális előjegyzési idő (óra)</label>
                  <input
                    type="number"
                    defaultValue="24"
                    className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-white">Naptár beállítások</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Calendar Sync */}
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-4">Naptár szinkronizáció</h4>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">📧</span>
                    <div>
                      <div className="text-white font-medium">Google Calendar</div>
                      <div className="text-gray-400 text-sm">Kétirányú szinkronizáció</div>
                    </div>
                  </div>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">
                    Csatlakozás
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">📅</span>
                    <div>
                      <div className="text-white font-medium">Microsoft Outlook</div>
                      <div className="text-gray-400 text-sm">Kétirányú szinkronizáció</div>
                    </div>
                  </div>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">
                    Csatlakozás
                  </button>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-4">Értesítési beállítások</h4>
              <div className="space-y-4">
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="text-blue-600" defaultChecked />
                  <span className="text-white">Email értesítések</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="text-blue-600" defaultChecked />
                  <span className="text-white">SMS emlékeztetők</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="text-blue-600" />
                  <span className="text-white">Push értesítések</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input type="checkbox" className="text-blue-600" defaultChecked />
                  <span className="text-white">Naptár szinkron értesítések</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && <BookingModal />}
    </div>
  );
};

const PaymentsModule = () => {
  const [activeTab, setActiveTab] = useState('invoices');
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Sample invoices data
  const sampleInvoices = [
    {
      id: 'INV-2024-001',
      client: 'Nagy Péter',
      email: 'peter.nagy@email.com',
      amount: 125000,
      currency: 'HUF',
      status: 'paid',
      dueDate: '2024-01-30',
      issueDate: '2024-01-15',
      items: [
        { name: 'Premium csomag', quantity: 1, price: 100000, vat: 25000 }
      ],
      paymentMethod: 'stripe',
      notes: 'Ingatlan értékelési szolgáltatás'
    },
    {
      id: 'INV-2024-002',
      client: 'Kovács Anna',
      email: 'anna.kovacs@company.hu',
      amount: 75000,
      currency: 'HUF',
      status: 'pending',
      dueDate: '2024-02-15',
      issueDate: '2024-01-20',
      items: [
        { name: 'Konzultáció', quantity: 2, price: 30000, vat: 15000 }
      ],
      paymentMethod: 'bank_transfer',
      notes: 'Helyszíni felmérés és tanácsadás'
    },
    {
      id: 'INV-2024-003',
      client: 'Szabó Gábor',
      email: 'gabor.szabo@startup.com',
      amount: 200000,
      currency: 'HUF',
      status: 'overdue',
      dueDate: '2024-01-10',
      issueDate: '2024-01-01',
      items: [
        { name: 'Enterprise csomag', quantity: 1, price: 160000, vat: 40000 }
      ],
      paymentMethod: 'barion',
      notes: 'Éves előfizetés'
    }
  ];

  // Sample products/services
  const sampleProducts = [
    {
      id: '1',
      name: 'Alapcsomag',
      description: 'Alapvető ingatlan értékelési szolgáltatás',
      price: 50000,
      currency: 'HUF',
      type: 'one-time',
      category: 'service',
      vatRate: 25,
      active: true
    },
    {
      id: '2',
      name: 'Premium csomag',
      description: 'Teljes körű ingatlan értékelés és tanácsadás',
      price: 100000,
      currency: 'HUF',
      type: 'one-time',
      category: 'service',
      vatRate: 25,
      active: true
    },
    {
      id: '3',
      name: 'Havi előfizetés',
      description: 'Korlátlan konzultációk havonta',
      price: 25000,
      currency: 'HUF',
      type: 'subscription',
      category: 'subscription',
      vatRate: 25,
      active: true,
      interval: 'monthly'
    }
  ];

  // Sample subscriptions
  const sampleSubscriptions = [
    {
      id: 'SUB-001',
      client: 'Nagy Péter',
      product: 'Havi előfizetés',
      amount: 25000,
      status: 'active',
      nextBilling: '2024-02-15',
      startDate: '2024-01-15',
      paymentMethod: 'stripe'
    },
    {
      id: 'SUB-002',
      client: 'Kovács Anna',
      product: 'Havi előfizetés',
      amount: 25000,
      status: 'cancelled',
      nextBilling: null,
      startDate: '2024-01-01',
      paymentMethod: 'barion'
    }
  ];

  useEffect(() => {
    setInvoices(sampleInvoices);
    setProducts(sampleProducts);
    setSubscriptions(sampleSubscriptions);
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-500';
      case 'pending': return 'bg-yellow-500';
      case 'overdue': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      case 'active': return 'bg-green-500';
      case 'cancelled': return 'bg-red-500';
      case 'paused': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const formatCurrency = (amount, currency = 'HUF') => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.client.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const InvoiceModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[#2a2a2a] rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">Új számla létrehozása</h2>
          <button
            onClick={() => setShowInvoiceModal(false)}
            className="text-gray-400 hover:text-white text-2xl"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Client Information */}
          <div className="space-y-4">
            <h3 className="text-white font-bold">Ügyfél adatok</h3>
            <div>
              <label className="block text-white font-medium mb-2">Ügyfél neve</label>
              <input
                type="text"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Teljes név vagy cégnév"
              />
            </div>
            <div>
              <label className="block text-white font-medium mb-2">Email cím</label>
              <input
                type="email"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-white font-medium mb-2">Számlázási cím</label>
              <textarea
                rows="3"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="Teljes számlázási cím"
              />
            </div>
          </div>

          {/* Invoice Details */}
          <div className="space-y-4">
            <h3 className="text-white font-bold">Számla adatok</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-medium mb-2">Kiállítás dátuma</label>
                <input
                  type="date"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-white font-medium mb-2">Esedékesség</label>
                <input
                  type="date"
                  className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-white font-medium mb-2">Fizetési mód</label>
              <select className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="bank_transfer">Banki átutalás</option>
                <option value="stripe">Stripe (kártya)</option>
                <option value="barion">Barion</option>
                <option value="cash">Készpénz</option>
              </select>
            </div>
            <div>
              <label className="block text-white font-medium mb-2">Megjegyzések</label>
              <textarea
                rows="3"
                className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                placeholder="További információk..."
              />
            </div>
          </div>
        </div>

        {/* Invoice Items */}
        <div className="mt-6">
          <h3 className="text-white font-bold mb-4">Számla tételek</h3>
          <div className="bg-[#1a1a1a] rounded-lg p-4">
            <div className="grid grid-cols-12 gap-2 text-gray-400 text-sm mb-3">
              <div className="col-span-5">Megnevezés</div>
              <div className="col-span-2">Mennyiség</div>
              <div className="col-span-2">Egységár</div>
              <div className="col-span-2">Összeg</div>
              <div className="col-span-1"></div>
            </div>
            <div className="grid grid-cols-12 gap-2 mb-3">
              <input
                type="text"
                placeholder="Szolgáltatás neve"
                className="col-span-5 bg-[#2a2a2a] border border-white/10 rounded px-2 py-1 text-white text-sm"
              />
              <input
                type="number"
                placeholder="1"
                className="col-span-2 bg-[#2a2a2a] border border-white/10 rounded px-2 py-1 text-white text-sm"
              />
              <input
                type="number"
                placeholder="0"
                className="col-span-2 bg-[#2a2a2a] border border-white/10 rounded px-2 py-1 text-white text-sm"
              />
              <div className="col-span-2 flex items-center text-white text-sm">0 HUF</div>
              <button className="col-span-1 text-red-400 hover:text-red-300">🗑️</button>
            </div>
            <button className="text-blue-400 hover:text-blue-300 text-sm">+ Új tétel hozzáadása</button>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-white/10">
          <button
            onClick={() => setShowInvoiceModal(false)}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            Mégse
          </button>
          <button className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors">
            Piszkozat mentése
          </button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors">
            Számla kiállítása
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <span className="text-3xl mr-3">💳</span>
            Payments & Invoices
          </h2>
          <p className="text-gray-400 mt-1">Számlázás és fizetés kezelése</p>
        </div>
        <button
          onClick={() => setShowInvoiceModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
        >
          <span className="mr-2">+</span>
          Új számla
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-[#2a2a2a] rounded-lg p-1">
        {[
          { id: 'invoices', label: 'Számlák', icon: '📄' },
          { id: 'products', label: 'Termékek', icon: '📦' },
          { id: 'subscriptions', label: 'Előfizetések', icon: '🔄' },
          { id: 'analytics', label: 'Analitika', icon: '📊' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a]'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'invoices' && (
        <div className="space-y-4">
          {/* Search and Filters */}
          <div className="flex items-center space-x-4">
            <input
              type="text"
              placeholder="Keresés számlákban..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select className="bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Minden státusz</option>
              <option value="paid">Kifizetve</option>
              <option value="pending">Függőben</option>
              <option value="overdue">Lejárt</option>
              <option value="draft">Piszkozat</option>
            </select>
          </div>

          {/* Invoices Table */}
          <div className="bg-[#2a2a2a] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a1a1a]">
                  <tr>
                    <th className="text-left p-4 text-gray-400 font-medium">Számla szám</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Ügyfél</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Összeg</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Státusz</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Esedékesség</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Műveletek</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-t border-white/5 hover:bg-[#3a3a3a]">
                      <td className="p-4">
                        <div className="text-white font-medium">{invoice.id}</div>
                        <div className="text-gray-400 text-sm">{formatDate(invoice.issueDate)}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-white">{invoice.client}</div>
                        <div className="text-gray-400 text-sm">{invoice.email}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-white font-medium">{formatCurrency(invoice.amount)}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${getStatusColor(invoice.status)}`}></div>
                          <span className="text-white capitalize">{invoice.status}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-white">{formatDate(invoice.dueDate)}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-400 hover:text-blue-300 text-sm">Megtekintés</button>
                          <button className="text-green-400 hover:text-green-300 text-sm">PDF</button>
                          <button className="text-yellow-400 hover:text-yellow-300 text-sm">Email</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'products' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Termékek és szolgáltatások</h3>
            <button
              onClick={() => setShowProductModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              + Új termék
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {products.map((product) => (
              <div key={product.id} className="bg-[#2a2a2a] rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h4 className="text-white font-bold text-lg">{product.name}</h4>
                    <p className="text-gray-400 text-sm">{product.description}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${product.active ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Ár:</span>
                    <span className="text-white font-bold">{formatCurrency(product.price)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">ÁFA:</span>
                    <span className="text-white">{product.vatRate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Típus:</span>
                    <span className="text-white capitalize">{product.type}</span>
                  </div>
                  {product.interval && (
                    <div className="flex justify-between">
                      <span className="text-gray-400">Gyakoriság:</span>
                      <span className="text-white">{product.interval === 'monthly' ? 'Havi' : 'Éves'}</span>
                    </div>
                  )}
                </div>

                <div className="flex space-x-2 mt-4 pt-4 border-t border-white/10">
                  <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded text-sm">
                    Szerkesztés
                  </button>
                  <button className="px-3 py-2 text-gray-400 hover:text-white">
                    {product.active ? '⏸️' : '▶️'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'subscriptions' && (
        <div className="space-y-4">
          <h3 className="text-xl font-bold text-white">Előfizetések kezelése</h3>
          
          <div className="bg-[#2a2a2a] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1a1a1a]">
                  <tr>
                    <th className="text-left p-4 text-gray-400 font-medium">Előfizetés ID</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Ügyfél</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Termék</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Összeg</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Státusz</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Következő számlázás</th>
                    <th className="text-left p-4 text-gray-400 font-medium">Műveletek</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((subscription) => (
                    <tr key={subscription.id} className="border-t border-white/5 hover:bg-[#3a3a3a]">
                      <td className="p-4">
                        <div className="text-white font-medium">{subscription.id}</div>
                        <div className="text-gray-400 text-sm">{formatDate(subscription.startDate)}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-white">{subscription.client}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-white">{subscription.product}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-white font-medium">{formatCurrency(subscription.amount)}</div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 rounded-full ${getStatusColor(subscription.status)}`}></div>
                          <span className="text-white capitalize">{subscription.status}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-white">
                          {subscription.nextBilling ? formatDate(subscription.nextBilling) : '-'}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center space-x-2">
                          <button className="text-blue-400 hover:text-blue-300 text-sm">Kezelés</button>
                          <button className="text-red-400 hover:text-red-300 text-sm">Lemondás</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-white">Pénzügyi analitika</h3>
          
          {/* Revenue Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <div className="text-3xl font-bold text-green-400">2,450,000</div>
              <div className="text-gray-400 text-sm">HUF bevétel (havi)</div>
            </div>
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <div className="text-3xl font-bold text-blue-400">18</div>
              <div className="text-gray-400 text-sm">Kiállított számlák</div>
            </div>
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <div className="text-3xl font-bold text-yellow-400">3</div>
              <div className="text-gray-400 text-sm">Lejárt számlák</div>
            </div>
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <div className="text-3xl font-bold text-purple-400">12</div>
              <div className="text-gray-400 text-sm">Aktív előfizetés</div>
            </div>
          </div>

          {/* Charts Placeholder */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-4">Havi bevétel</h4>
              <div className="h-64 bg-[#1a1a1a] rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2">📈</div>
                  <p className="text-gray-400">Bevételi grafikon</p>
                  <p className="text-gray-500 text-sm">Chart.js integráció szükséges</p>
                </div>
              </div>
            </div>

            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-4">Fizetési módok</h4>
              <div className="h-64 bg-[#1a1a1a] rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="text-4xl mb-2">🥧</div>
                  <p className="text-gray-400">Fizetési módok eloszlása</p>
                  <p className="text-gray-500 text-sm">Chart.js integráció szükséges</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      {showInvoiceModal && <InvoiceModal />}
    </div>
  );
};

const SitesModule = () => {
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeTab, setActiveTab] = useState('sites');

  const sampleSites = [
    {
      id: '1',
      name: 'HoppHomes Főoldal',
      domain: 'hopphomes.com',
      status: 'published',
      template: 'business',
      visits: 1250,
      conversions: 45,
      lastUpdated: '2024-01-20T10:30:00Z'
    },
    {
      id: '2', 
      name: 'Ingatlan Landing Page',
      domain: 'ingatlan.hopphomes.com',
      status: 'draft',
      template: 'landing',
      visits: 890,
      conversions: 23,
      lastUpdated: '2024-01-19T15:45:00Z'
    }
  ];

  useEffect(() => {
    setSites(sampleSites);
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">🌐</span>
          Webhelyek
        </h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Új Webhely</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sites.map((site) => (
          <div key={site.id} className="bg-[#2a2a2a] rounded-xl p-6 hover:bg-[#3a3a3a] transition-all duration-200 border border-white/5 hover:border-white/10">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-3 text-white">
                <div className="text-2xl">🌐</div>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                site.status === 'published' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'
              }`}>
                {site.status === 'published' ? 'Közzétéve' : 'Vázlat'}
              </div>
            </div>

            <h4 className="text-white font-bold text-lg mb-1">{site.name}</h4>
            <p className="text-gray-400 text-sm mb-4">{site.domain}</p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">{site.visits}</div>
                <div className="text-xs text-gray-400">Látogatás</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{site.conversions}</div>
                <div className="text-xs text-gray-400">Konverzió</div>
              </div>
            </div>

            <div className="flex space-x-2">
              <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors">
                Szerkesztés
              </button>
              <button className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm transition-colors">
                Előnézet
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ConversationsModule = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [searchTerm, setSearchTerm] = useState('');

  const sampleConversations = [
    {
      id: '1',
      contact: 'Nagy Péter',
      email: 'peter.nagy@email.com',
      phone: '+36 30 123 4567',
      lastMessage: 'Érdekelne az ingatlan ajánlat',
      timestamp: '2024-01-20T14:30:00Z',
      unread: true,
      channel: 'email',
      status: 'active'
    },
    {
      id: '2',
      contact: 'Kovács Anna',
      email: 'anna.kovacs@email.com',
      phone: '+36 70 987 6543',
      lastMessage: 'Köszönöm a gyors választ!',
      timestamp: '2024-01-20T13:15:00Z',
      unread: false,
      channel: 'sms',
      status: 'resolved'
    }
  ];

  useEffect(() => {
    setConversations(sampleConversations);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">💬</span>
          Beszélgetések
        </h2>
        <div className="flex space-x-3">
          <input
            type="text"
            placeholder="Keresés..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-[#2a2a2a] border border-white/10 rounded-lg px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex space-x-1 bg-[#2a2a2a] rounded-lg p-1">
        {[
          { id: 'inbox', name: 'Beérkező', icon: '📥' },
          { id: 'sent', name: 'Elküldött', icon: '📤' },
          { id: 'archived', name: 'Archivált', icon: '📁' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          {conversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => setSelectedConversation(conversation)}
              className={`bg-[#2a2a2a] rounded-xl p-4 cursor-pointer transition-all duration-200 border ${
                selectedConversation?.id === conversation.id
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-white/5 hover:border-white/10 hover:bg-[#3a3a3a]'
              }`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {conversation.contact.charAt(0)}
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{conversation.contact}</h4>
                    <p className="text-gray-400 text-sm">{conversation.email}</p>
                  </div>
                </div>
                {conversation.unread && (
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                )}
              </div>
              <p className="text-gray-300 text-sm mb-2 line-clamp-2">{conversation.lastMessage}</p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">
                  {new Date(conversation.timestamp).toLocaleString('hu-HU')}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  conversation.channel === 'email' ? 'bg-blue-500/20 text-blue-400' :
                  conversation.channel === 'sms' ? 'bg-green-500/20 text-green-400' :
                  'bg-purple-500/20 text-purple-400'
                }`}>
                  {conversation.channel}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selectedConversation ? (
            <div className="bg-[#2a2a2a] rounded-xl p-6 h-96">
              <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/10">
                <div>
                  <h3 className="text-white font-bold">{selectedConversation.contact}</h3>
                  <p className="text-gray-400 text-sm">{selectedConversation.email}</p>
                </div>
                <div className="flex space-x-2">
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm">
                    Válasz
                  </button>
                  <button className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm">
                    Archív
                  </button>
                </div>
              </div>
              <div className="text-center text-gray-400 py-8">
                <div className="text-4xl mb-2">💬</div>
                <p>Beszélgetés részletek itt jelennek meg</p>
              </div>
            </div>
          ) : (
            <div className="bg-[#2a2a2a] rounded-xl p-6 h-96 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <div className="text-6xl mb-4">💬</div>
                <p>Válassz egy beszélgetést a megtekintéshez</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CampaignsModule = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [activeTab, setActiveTab] = useState('email');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const sampleCampaigns = [
    {
      id: '1',
      name: 'Új ingatlan ajánlatok',
      type: 'email',
      status: 'active',
      recipients: 1250,
      sent: 1200,
      opened: 480,
      clicked: 96,
      created: '2024-01-15',
      scheduled: '2024-01-22T10:00:00Z'
    },
    {
      id: '2',
      name: 'SMS emlékeztető',
      type: 'sms',
      status: 'completed',
      recipients: 500,
      sent: 500,
      opened: 450,
      clicked: 45,
      created: '2024-01-10',
      scheduled: '2024-01-20T14:00:00Z'
    }
  ];

  useEffect(() => {
    setCampaigns(sampleCampaigns);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">📢</span>
          Marketing Kampányok
        </h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2"
        >
          <span>➕</span>
          <span>Új Kampány</span>
        </button>
      </div>

      <div className="flex space-x-1 bg-[#2a2a2a] rounded-lg p-1">
        {[
          { id: 'email', name: 'Email', icon: '📧' },
          { id: 'sms', name: 'SMS', icon: '📱' },
          { id: 'social', name: 'Social', icon: '📱' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
              activeTab === tab.id
                ? 'bg-purple-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.name}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {campaigns.filter(campaign => campaign.type === activeTab).map((campaign) => (
          <div key={campaign.id} className="bg-[#2a2a2a] rounded-xl p-6 hover:bg-[#3a3a3a] transition-all duration-200">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-white font-bold mb-1">{campaign.name}</h3>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                  campaign.status === 'active' ? 'bg-green-500/20 text-green-400' :
                  campaign.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-yellow-500/20 text-yellow-400'
                }`}>
                  {campaign.status === 'active' ? '🟢' : campaign.status === 'completed' ? '✅' : '⏳'}
                  <span className="ml-1 capitalize">{campaign.status}</span>
                </div>
              </div>
              <button className="text-gray-400 hover:text-white">⋮</button>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Címzettek:</span>
                <span className="text-white font-medium">{campaign.recipients.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Elküldve:</span>
                <span className="text-white font-medium">{campaign.sent.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Megnyitva:</span>
                <span className="text-white font-medium">
                  {campaign.opened.toLocaleString()} ({((campaign.opened / campaign.sent) * 100).toFixed(1)}%)
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Kattintás:</span>
                <span className="text-white font-medium">
                  {campaign.clicked.toLocaleString()} ({((campaign.clicked / campaign.sent) * 100).toFixed(1)}%)
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 flex space-x-2">
              <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm transition-colors">
                Szerkesztés
              </button>
              <button className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm transition-colors">
                Jelentés
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ReputationModule = () => (
  <div className="text-center py-12">
    <div className="text-6xl mb-4">⭐</div>
    <h3 className="text-2xl font-bold text-white mb-2">Hírnév Kezelés</h3>
    <p className="text-gray-400 mb-6">Vélemény gyűjtés és kezelés automatizált kampányokkal</p>
    <div className="bg-[#2a2a2a] rounded-xl p-6 text-left">
      <h4 className="text-white font-bold mb-3">Elérhető funkciók:</h4>
      <ul className="text-gray-300 space-y-2">
        <li>• Értékelés gyűjtő kampányok</li>
        <li>• Google Business Reviews integráció</li>
        <li>• Review landing page template-ek</li>
        <li>• Negatív vélemény riasztások</li>
í        <li>• Válasz sablonok és automatizáció</li>
        <li>• Reputáció monitoring dashboard</li>
      </ul>
    </div>
  </div>
);

const ReportingModule = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');
  const [selectedTenant, setSelectedTenant] = useState('all');

  const overviewStats = {
    totalContacts: 2847,
    totalDeals: 156,
    totalRevenue: 12450000,
    conversionRate: 18.5,
    avgDealValue: 79800,
    activeAutomations: 23,
    emailOpenRate: 24.8,
    formSubmissions: 892
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency: 'HUF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value) => `${value.toFixed(1)}%`;

  const KPICard = ({ title, value, change, changeType, icon, color }) => (
    <div className="bg-[#2a2a2a] rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className={`text-3xl ${color}`}>{icon}</div>
        <div className={`text-sm px-2 py-1 rounded-full ${
          changeType === 'positive' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
        }`}>
          {changeType === 'positive' ? '↗' : '↘'} {change}
        </div>
      </div>
      <div className="text-2xl font-bold text-white mb-1">{value}</div>
      <div className="text-gray-400 text-sm">{title}</div>
    </div>
  );

  const ChartPlaceholder = ({ title, type }) => (
    <div className="bg-[#2a2a2a] rounded-xl p-6">
      <h4 className="text-white font-bold mb-4">{title}</h4>
      <div className="bg-[#1a1a1a] rounded-lg h-64 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-2">{type === 'line' ? '📈' : '📊'}</div>
          <p className="text-gray-400">{title}</p>
          <p className="text-gray-500 text-sm">Chart.js integráció szükséges</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center">
            <span className="text-3xl mr-3">📈</span>
            Reporting & Analytics
          </h2>
          <p className="text-gray-400 mt-1">Átfogó jelentések és elemzések</p>
        </div>
        <div className="flex items-center space-x-3">
          <select className="bg-[#2a2a2a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
            <option>Összes tenant</option>
            <option>HoppHomes Budapest</option>
          </select>
          <select className="bg-[#2a2a2a] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
            <option>Utolsó 30 nap</option>
            <option>Utolsó 90 nap</option>
          </select>
          <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg text-sm">CSV</button>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm">Excel</button>
        </div>
      </div>

      <div className="flex space-x-1 bg-[#2a2a2a] rounded-lg p-1">
        {[
          { id: 'overview', label: 'Áttekintés', icon: '📊' },
          { id: 'sales', label: 'Értékesítés', icon: '💰' },
          { id: 'marketing', label: 'Marketing', icon: '📢' },
          { id: 'custom', label: 'Egyedi riportok', icon: '📋' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center justify-center ${
              activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a]'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <KPICard title="Összes kontakt" value={overviewStats.totalContacts.toLocaleString()} change="+12.5%" changeType="positive" icon="👥" color="text-blue-400" />
            <KPICard title="Aktív üzletek" value={overviewStats.totalDeals.toLocaleString()} change="+8.3%" changeType="positive" icon="🤝" color="text-green-400" />
            <KPICard title="Összes bevétel" value={formatCurrency(overviewStats.totalRevenue)} change="+15.7%" changeType="positive" icon="💰" color="text-yellow-400" />
            <KPICard title="Konverziós ráta" value={formatPercentage(overviewStats.conversionRate)} change="+2.1%" changeType="positive" icon="📈" color="text-purple-400" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartPlaceholder title="Havi bevétel alakulása" type="line" />
            <ChartPlaceholder title="Pipeline teljesítmény" type="bar" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#2a2a2a] rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{formatCurrency(overviewStats.avgDealValue)}</div>
              <div className="text-gray-400 text-sm">Átlag üzlet érték</div>
            </div>
            <div className="bg-[#2a2a2a] rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{formatPercentage(overviewStats.emailOpenRate)}</div>
              <div className="text-gray-400 text-sm">Email megnyitási ráta</div>
            </div>
            <div className="bg-[#2a2a2a] rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-white">{overviewStats.formSubmissions}</div>
              <div className="text-gray-400 text-sm">Űrlap kitöltések</div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'sales' && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-white">Értékesítési jelentések</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <KPICard title="Havi bevétel" value={formatCurrency(1650000)} change="+16.2%" changeType="positive" icon="💵" color="text-green-400" />
            <KPICard title="Lezárt üzletek" value="23" change="+4" changeType="positive" icon="✅" color="text-blue-400" />
            <KPICard title="Win rate" value="67.6%" change="+3.2%" changeType="positive" icon="🏆" color="text-yellow-400" />
            <KPICard title="Átlag ciklus idő" value="14.2 nap" change="-2.1 nap" changeType="positive" icon="⏱️" color="text-purple-400" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartPlaceholder title="Értékesítési tölcsér" type="bar" />
            <ChartPlaceholder title="Havi teljesítmény trend" type="line" />
          </div>
        </div>
      )}

      {activeTab === 'marketing' && (
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-white">Marketing teljesítmény</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <KPICard title="Email kampányok" value="12" change="+3" changeType="positive" icon="📧" color="text-blue-400" />
            <KPICard title="Megnyitási ráta" value={formatPercentage(overviewStats.emailOpenRate)} change="+1.2%" changeType="positive" icon="👁️" color="text-green-400" />
            <KPICard title="Kattintási ráta" value="6.8%" change="+0.5%" changeType="positive" icon="👆" color="text-purple-400" />
            <KPICard title="Lead generálás" value="156" change="+23" changeType="positive" icon="🎯" color="text-yellow-400" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartPlaceholder title="Kampány teljesítmény" type="bar" />
            <ChartPlaceholder title="Lead források" type="bar" />
          </div>
        </div>
      )}

      {activeTab === 'custom' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">Egyedi riportok</h3>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg">+ Új riport</button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-2">Havi értékesítési összesítő</h4>
              <p className="text-gray-400 text-sm mb-4">Részletes értékesítési adatok havi bontásban</p>
              <div className="flex space-x-2">
                <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm">Futtatás</button>
                <button className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm">Ütemezés</button>
              </div>
            </div>
            <div className="bg-[#2a2a2a] rounded-xl p-6">
              <h4 className="text-white font-bold mb-2">Marketing ROI elemzés</h4>
              <p className="text-gray-400 text-sm mb-4">Kampányok megtérülési mutatói</p>
              <div className="flex space-x-2">
                <button className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm">Futtatás</button>
                <button className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm">Ütemezés</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsModule = () => {
  const [activeSettingsTab, setActiveSettingsTab] = useState('rbac');
  const [systemSettings, setSystemSettings] = useState({
    companyName: 'HoppHomes',
    domain: 'hopphomes.com',
    timezone: 'Europe/Budapest',
    language: 'hu',
    currency: 'HUF',
    emailProvider: 'smtp',
    smsProvider: 'twilio',
    storageProvider: 'firebase'
  });

  const settingsTabs = [
    { id: 'rbac', name: 'RBAC Kezelés', icon: '🔐' },
    { id: 'general', name: 'Általános', icon: '⚙️' },
    { id: 'integrations', name: 'Integrációk', icon: '🔗' },
    { id: 'security', name: 'Biztonság', icon: '🛡️' },
    { id: 'branding', name: 'Márkaépítés', icon: '🎨' },
    { id: 'audit', name: 'Audit Log', icon: '📋' }
  ];

  const GeneralSettings = () => (
    <div className="space-y-6">
      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Alapbeállítások</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white font-medium mb-2">Cég neve</label>
            <input
              type="text"
              value={systemSettings.companyName}
              onChange={(e) => setSystemSettings({...systemSettings, companyName: e.target.value})}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Domain</label>
            <input
              type="text"
              value={systemSettings.domain}
              onChange={(e) => setSystemSettings({...systemSettings, domain: e.target.value})}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Időzóna</label>
            <select
              value={systemSettings.timezone}
              onChange={(e) => setSystemSettings({...systemSettings, timezone: e.target.value})}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Europe/Budapest">Europe/Budapest</option>
              <option value="Europe/London">Europe/London</option>
              <option value="America/New_York">America/New_York</option>
              <option value="Asia/Tokyo">Asia/Tokyo</option>
            </select>
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Nyelv</label>
            <select
              value={systemSettings.language}
              onChange={(e) => setSystemSettings({...systemSettings, language: e.target.value})}
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="hu">Magyar</option>
              <option value="en">English</option>
              <option value="de">Deutsch</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const IntegrationsSettings = () => (
    <div className="space-y-6">
      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Email Szolgáltató</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { id: 'smtp', name: 'SMTP', icon: '📧', description: 'Egyéni SMTP szerver' },
            { id: 'sendgrid', name: 'SendGrid', icon: '📨', description: 'SendGrid API' },
            { id: 'mailgun', name: 'Mailgun', icon: '📮', description: 'Mailgun szolgáltatás' }
          ].map((provider) => (
            <div
              key={provider.id}
              onClick={() => setSystemSettings({...systemSettings, emailProvider: provider.id})}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                systemSettings.emailProvider === provider.id
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-white/10 hover:border-white/[0.08]'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">{provider.icon}</div>
                <h4 className="text-white font-bold">{provider.name}</h4>
                <p className="text-gray-400 text-sm">{provider.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">SMS Szolgáltató</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { id: 'twilio', name: 'Twilio', icon: '📱', description: 'Twilio SMS API' },
            { id: 'nexmo', name: 'Vonage', icon: '📲', description: 'Vonage (Nexmo) API' },
            { id: 'custom', name: 'Egyéni', icon: '🔧', description: 'Egyéni SMS gateway' }
          ].map((provider) => (
            <div
              key={provider.id}
              onClick={() => setSystemSettings({...systemSettings, smsProvider: provider.id})}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                systemSettings.smsProvider === provider.id
                  ? 'border-green-500 bg-green-500/10'
                  : 'border-white/10 hover:border-white/[0.08]'
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-2">{provider.icon}</div>
                <h4 className="text-white font-bold">{provider.name}</h4>
                <p className="text-gray-400 text-sm">{provider.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const SecuritySettings = () => (
    <div className="space-y-6">
      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Biztonsági Beállítások</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">Kétfaktoros hitelesítés</h4>
              <p className="text-gray-400 text-sm">Extra biztonság minden felhasználó számára</p>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="w-5 h-5 text-blue-600" defaultChecked />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">Session timeout</h4>
              <p className="text-gray-400 text-sm">Automatikus kijelentkezés inaktivitás után</p>
            </div>
            <select className="bg-[#2a2a2a] border border-white/10 rounded px-3 py-1 text-white">
              <option value="30">30 perc</option>
              <option value="60">1 óra</option>
              <option value="120">2 óra</option>
              <option value="480">8 óra</option>
            </select>
          </div>

          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">IP whitelist</h4>
              <p className="text-gray-400 text-sm">Csak megadott IP címekről való hozzáférés</p>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">GDPR Beállítások</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">Adatmegőrzési idő</h4>
              <p className="text-gray-400 text-sm">Inaktív felhasználói adatok törlése</p>
            </div>
            <select className="bg-[#2a2a2a] border border-white/10 rounded px-3 py-1 text-white">
              <option value="365">1 év</option>
              <option value="730">2 év</option>
              <option value="1095">3 év</option>
              <option value="1825">5 év</option>
            </select>
          </div>

          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">Cookie beleegyezés</h4>
              <p className="text-gray-400 text-sm">Cookie banner megjelenítése</p>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="w-5 h-5 text-blue-600" defaultChecked />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const BrandingSettings = () => (
    <div className="space-y-6">
      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">White-label Beállítások</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-white font-medium mb-2">Logó URL</label>
            <input
              type="url"
              placeholder="https://example.com/logo.png"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Favicon URL</label>
            <input
              type="url"
              placeholder="https://example.com/favicon.ico"
              className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Elsődleges szín</label>
            <input
              type="color"
              defaultValue="#3B82F6"
              className="w-full h-12 bg-[#1a1a1a] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-white font-medium mb-2">Másodlagos szín</label>
            <input
              type="color"
              defaultValue="#8B5CF6"
              className="w-full h-12 bg-[#1a1a1a] border border-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Egyéni CSS</h3>
        <textarea
          rows="8"
          placeholder="/* Egyéni CSS szabályok */&#10;.custom-button {&#10;  background: linear-gradient(45deg, #ff6b6b, #4ecdc4);&#10;}"
          className="w-full bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-2 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>
  );

  const AuditLogSettings = () => (
    <div className="space-y-6">
      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Audit Log Beállítások</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">Felhasználói műveletek naplózása</h4>
              <p className="text-gray-400 text-sm">Minden felhasználói művelet rögzítése</p>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="w-5 h-5 text-blue-600" defaultChecked />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">API hívások naplózása</h4>
              <p className="text-gray-400 text-sm">Külső API hívások rögzítése</p>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="w-5 h-5 text-blue-600" defaultChecked />
            </div>
          </div>

          <div className="flex items-center justify-between p-4 bg-[#1a1a1a] rounded-lg">
            <div>
              <h4 className="text-white font-medium">Adatváltozások naplózása</h4>
              <p className="text-gray-400 text-sm">Adatbázis módosítások követése</p>
            </div>
            <div className="flex items-center">
              <input type="checkbox" className="w-5 h-5 text-blue-600" defaultChecked />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#2a2a2a] rounded-xl p-6">
        <h3 className="text-xl font-bold text-white mb-4">Legutóbbi Audit Bejegyzések</h3>
        <div className="space-y-3">
          {[
            { user: 'Admin Péter', action: 'Felhasználó létrehozva', target: 'anna.manager@client1.com', time: '2024-01-22 10:30', type: 'create' },
            { user: 'Manager Anna', action: 'Kontakt módosítva', target: 'Nagy Péter', time: '2024-01-22 09:45', type: 'update' },
            { user: 'Sales Gábor', action: 'Deal áthelyezve', target: 'Tech Solutions deal', time: '2024-01-22 09:15', type: 'move' },
            { user: 'System', action: 'Automatizáció futtatva', target: 'Welcome Series', time: '2024-01-22 08:00', type: 'automation' }
          ].map((log, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-2 h-2 rounded-full ${
                  log.type === 'create' ? 'bg-green-500' :
                  log.type === 'update' ? 'bg-blue-500' :
                  log.type === 'move' ? 'bg-yellow-500' :
                  'bg-purple-500'
                }`}></div>
                <div>
                  <div className="text-white text-sm font-medium">{log.user} - {log.action}</div>
                  <div className="text-gray-400 text-xs">{log.target}</div>
                </div>
              </div>
              <div className="text-gray-500 text-xs">{log.time}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white flex items-center">
          <span className="text-3xl mr-3">⚙️</span>
          Beállítások
        </h2>
      </div>

      {/* Settings Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 bg-[#2a2a2a] rounded-lg p-1">
        {settingsTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveSettingsTab(tab.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSettingsTab === tab.id
                ? 'bg-blue-600 text-white'
                : 'text-gray-400 hover:text-white hover:bg-[#3a3a3a]'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.name}</span>
          </button>
        ))}
      </div>

      {/* Settings Content */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 p-6">
        {activeSettingsTab === 'rbac' && <RBACManager />}
        {activeSettingsTab === 'general' && <GeneralSettings />}
        {activeSettingsTab === 'integrations' && <IntegrationsSettings />}
        {activeSettingsTab === 'security' && <SecuritySettings />}
        {activeSettingsTab === 'branding' && <BrandingSettings />}
        {activeSettingsTab === 'audit' && <AuditLogSettings />}
      </div>
    </div>
  );
};


export default HoppXGHLPro;
